import { Button, Icon, Menu, Text } from "@ttc3k/trekker";
import { useOrganizationNameByIdQuery } from "gql/generated.ts";
import {
  NavigationBlocker,
  SimpleErrorNotification,
  SimpleLoader,
} from "shared/components";
import {
  Calendar,
  Home,
  KeyCommand,
  List,
  NavArrowDown,
  Settings,
  SystemRestart,
} from "iconoir-react";
import { useCurrentAppRoute } from "shared/hooks";
import {
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Divider } from "styled-system/jsx";
import { getMultilingualString } from "shared/utils";
import { css } from "styled-system/css";

type OrganizationMenuProps = {
  organizationId: string;
};

type MenuItem = {
  Icon: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  key: string;
  path: string;
  disabled?: boolean;
  hidden?: boolean;
};

const menuItems: MenuItem[] = [
  { Icon: Home, key: "HOME", path: "home" },
  { Icon: KeyCommand, key: "DIRECTORY", path: "directory" },
  { Icon: List, key: "CUSTOM_LISTS", path: "custom-lists" },
  /* { Icon: StatsReport, key: "CAMPAIGNS", path: "campaigns" }, */
  { Icon: SystemRestart, key: "WIDGETS", path: "widgets" },
  { Icon: Calendar, key: "EVENTS", path: "events" },
  {
    Icon: Settings,
    key: "MY_ORGANIZATION",
    path: "profile",
    hidden: true,
  },
];

export const OrganizationMenu = ({ organizationId }: OrganizationMenuProps) => {
  const { t } = useTranslation();
  const { data, error, loading } = useOrganizationNameByIdQuery({
    variables: { id: organizationId },
  });
  const appRoute = useCurrentAppRoute();
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState(menuItems[0]);

  const handleAppSwitcherClick = (item: { value: string }) => {
    setIsMenuOpen(false);
    setSelectedItem(
      menuItems.find((menuItem) => menuItem.path === item.value) ??
        menuItems[0],
    );
    const route = item.value
      ? `${appRoute.root}/${item.value}`
      : `${appRoute.root}`;
    navigate(route);
  };

  useEffect(() => {
    setSelectedItem(
      menuItems.find((menuItem) => menuItem.path === appRoute.appPath) ??
        menuItems[0],
    );
  }, [appRoute]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  if (loading) return <SimpleLoader />;

  if (error) return <SimpleErrorNotification />;

  if (!data || !data.organizationById) {
    return null;
  }

  return (
    <>
      <Text
        visual={"smallSemiBold"}
        className={css({ textAlign: "right" })}
        flex="1"
      >
        {getMultilingualString(data.organizationById.name)}
      </Text>
      <Divider
        orientation={"vertical"}
        thickness={"2px"}
        height={"16px"}
        color={"border.light"}
      />
      <NavigationBlocker onStayHereClick={() => setIsMenuOpen(false)} flex="1">
        <Menu.Root
          open={isMenuOpen}
          onPointerDownOutside={() => setIsMenuOpen(false)}
          trigger={
            <Button
              visual="unstyled"
              color={"text.mid"}
              display={"flex"}
              flexDir={"row"}
              alignItems={"center"}
              gap={75}
              borderRadius={"25"}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Text visual={"smallSemiBold"}>
                {t(`core:${selectedItem.key}`)}
              </Text>
              <Icon Element={NavArrowDown} color={"icon.light"} />
            </Button>
          }
          positioning={{
            placement: "bottom",
            offset: {
              crossAxis: -75,
            },
          }}
          onSelect={(item: { value: string }) => handleAppSwitcherClick(item)}
        >
          {menuItems.map(
            (item) =>
              !item.hidden && (
                <Menu.Item
                  value={item.path}
                  disabled={item.disabled}
                  justifyContent={"flex-start"}
                  gap={"200"}
                  key={item.key}
                >
                  <Icon Element={item.Icon} />
                  <Text visual={"smallRegular"}>{t(`core:${item.key}`)}</Text>
                </Menu.Item>
              ),
          )}
        </Menu.Root>
      </NavigationBlocker>
    </>
  );
};
